import { ButtonPremium, ProtectedComponent } from '@components'

import { ACCEPT_DEFAULT, useDropzoneInput } from '@billy/design-system'
import { MouseEvent, ReactElement, useCallback } from 'react'
import { FileRejection } from 'react-dropzone'
import { useTranslation } from 'react-i18next'

import { openFileDialog } from '@features/attachments/elements/ChooseFileDropdown/utils/openFileDialog'

import { Scope } from '../../../../enums/scope'
import { useOrgUsesReceiptsOnly } from '../../../../hooks/useOrgUsesReceiptsOnly'
import { WithTrackingContext } from '../../../../types/withTrackingContext'
import { useReceiptUploadGate } from '../../contexts/ReceiptUploadGateContext'

interface ReceiptUploadButtonProps extends WithTrackingContext {
  isUploading?: boolean
  onFilesSelect: (files: File[], filesRejected?: FileRejection[]) => void
}

export const ReceiptUploadButton = ({
  isUploading,
  onFilesSelect,
  trackingContext,
}: ReceiptUploadButtonProps): ReactElement => {
  const { t } = useTranslation()
  const orgUsesReceiptsOnly = useOrgUsesReceiptsOnly()
  const { gateUpload } = useReceiptUploadGate()

  const { inputRef, inputProps } = useDropzoneInput({
    accept: ACCEPT_DEFAULT,
    multiple: true,
    onFilesSelect,
  })

  const openPicker = useCallback(() => {
    openFileDialog(inputRef)
  }, [inputRef])

  const handleButtonClick = useCallback(
    (_event: MouseEvent<HTMLButtonElement>) => {
      if (orgUsesReceiptsOnly) {
        gateUpload(openPicker)
        return
      }

      openPicker()
    },
    [gateUpload, openPicker, orgUsesReceiptsOnly],
  )

  const buttonProps = {
    children: t('upload_files'),
    icon: 'image' as const,
    loading: isUploading,
    onClick: handleButtonClick,
    trackingContext,
  }

  return (
    <>
      <ProtectedComponent scopes={Scope.UploadsWrite} unauthorizedNode={<ButtonPremium {...buttonProps} isBlocked />}>
        <ButtonPremium {...buttonProps} />
      </ProtectedComponent>
      <input ref={inputRef} {...inputProps} />
    </>
  )
}
